import React, {useState, useEffect} from "react";
import { useNavigate } from 'react-router';
import {ApplicationInterface, KeyValueInterface} from "./interfaces";
import Api from "../../services/Api";
import StepOne from "./components/StepOne"
import StepTwo from "./components/StepTwo"
import StepThree from "./components/StepThree"
import StepFour from "./components/StepFour"
import StepFive from "./components/StepFive"
import StepSix from "./components/StepSix"
import StepSeven from "./components/StepSeven"
import StepEight from "./components/StepEight"
import StepNine from "./components/StepNine"
import StepTen from "./components/StepTen";
import ProgressBar from "../../components/Progress/ProgressBar";
import iconBadge from "url:shared-assets/icons/badge-check.svg"
import iconClock from "url:shared-assets/icons/clock.svg";


const ApplicationPage = () => {
    const api = Api.getInstance();
    const navigate = useNavigate();
    const [application, setApplication] = useState<ApplicationInterface|undefined>();
    const [experiencesByArea, setExperiencesByArea] = useState<Array<KeyValueInterface>>([]);
    const [experiencesByIndustry, setExperiencesByIndustry] = useState<Array<KeyValueInterface>>([]);
    const [leadershipLevels, setLeadershipLevels] = useState<Array<KeyValueInterface>>([]);
    const [professionalAreas, setProfessionalAreas] = useState<Array<KeyValueInterface>>([]);
    const [email, setEmail] = useState<string>("");
    const [step, setStep] = useState(0);
    const [stepsDone, setStepsDone] = useState<boolean[]>([false, false, false, false]);
    const [applicationSent, setApplicationSent] = useState<boolean>(false)
    const [applied, setApplied] = useState<boolean>(false)
    const [onboarded, setOnboarded] = useState<boolean>(false)

    useEffect(() => {
        getData()
    }, [])

    const getData = () => {
        api.get('coach/application').then(response => {
            const _application: ApplicationInterface = response.json.application
            setApplication(_application);
            setExperiencesByArea(response.json.experiencesByArea)
            setExperiencesByIndustry(response.json.experiencesByIndustry)
            setLeadershipLevels(response.json.leadershipLevels)
            setProfessionalAreas(response.json.professionalAreas)
            calculateStep(_application);
            setApplied(response.json.applied)
            setOnboarded(response.json.onboarded)
        })
    }

    const saveData = (_application: ApplicationInterface)=> {
        api.post('coach/application/save/' + step, _application).then(() => {
            getData()
        })
    }

    const sendApplication = () => {
        api.post('coach/application/save/9', {}).then(response => {
            setEmail(response.json.email)
            setApplicationSent(true)
        })
    }

    const calculateStep = (_application: ApplicationInterface) => {
        let step:number;
        if (_application.organisationNumber.length < 1) {
            // Not completed first page, set step 0
            step = 0
        } else if (_application.coachingHours < 200) {
            step = 1
        } else if (_application.linkedinUrl.length < 1) {
            step = 2
        } else if (_application.languagesNative.length === 0 && _application.languagesProfessional.length === 0) {
            step = 3
        } else if (_application.experienceByArea.length === 0) {
            step = 4
        } else if (_application.experienceByIndustry.length === 0) {
            step = 5
        } else if (_application.experienceByRole.length === 0) {
            step = 6
        } else if (_application.experienceProfessional.length === 0) {
            step = 7
        } else if (_application.workedInIndustry.length === 0) {
            step = 8
        } else {
            step = 9
        }

        // We should now be at page 3 - select coach
        setStep(step)
        let _stepsDone = []
        for (let i = 0; i < 10; i++) {
            let done = step > i;
            _stepsDone.push(done)
        }
        setStepsDone(_stepsDone)
    }

    if (application === undefined) {
        return (
            <div className="mt-8">Loading...</div>
        )
    }

    const changeStep = (newStep: number) => {
        if (newStep === 0 || stepsDone[newStep -1]) {
            setStep(newStep);
        }
    }

    const selectStep = () => {
        if (step === 9) {
            return <StepTen application={application} save={sendApplication} changeStep={changeStep}
                            experiencesByArea={experiencesByArea}
                            experiencesByIndustry={experiencesByIndustry}
                            leadershipLevels={leadershipLevels}
                            professionalAreas={professionalAreas}
                    />
        } else if (step === 8) {
            return <StepNine application={application} save={saveData} experiencesByIndustry={experiencesByIndustry}/>
        } else if (step === 7) {
            return <StepEight application={application} save={saveData} professionalAreas={professionalAreas} />
        } else if (step === 6) {
            return <StepSeven application={application} save={saveData} leadershipLevels={leadershipLevels}/>
        } else if (step === 5) {
            return <StepSix application={application} save={saveData} experiencesByIndustry={experiencesByIndustry}/>
        } else if (step === 4) {
            return <StepFive application={application} save={saveData} experiencesByArea={experiencesByArea}/>
        } else if (step === 3) {
            return <StepFour application={application} save={saveData}/>
        } else if (step === 2) {
            return <StepThree application={application} save={saveData}/>
        } else if (step === 1) {
            return <StepTwo application={application} save={saveData}/>
        } else {
            return <StepOne application={application} save={saveData}/>
        }
    }

    if (applied) {
        if (onboarded) {
            navigate('/')
        } else {
            return (
              <div className="mt-10 flex flex-col justify-center">
                  <img alt="check" src={iconClock} className="h-12 w-12 mx-auto"/>
                  <div className="mt-6 text-2xl font-medium text-center">
                      Waiting for approval
                  </div>
                  <div className="mt-2 text-neutral-400 text-center">
                      We're currently reviewing your application and will notify you via email as soon as we have an update.
                  </div>
                  <div className="mt-4 text-neutral-400 text-center">
                      Thanks for your patience and enthusiasm!
                  </div>
              </div>
            )
        }
    }

    if (applicationSent) {
        return (
          <div className="mt-10 flex justify-center">
              <div>
                  <img alt="check" src={iconBadge} className="h-12"/>
                  <div className="mt-6 text-2xl font-medium">
                      Application sent
                  </div>
                  <div className="mt-2 text-neutral-400">
                      Thanks for taking your time to apply!
                      We'll get back to you as soon as possible via {email}
                  </div>
              </div>

          </div>
        )
    }

    return (
        <div className="m-4 main-content">
            <div>
                <ProgressBar step={step + 1} steps={stepsDone.length}/>
            </div>
            {
                selectStep()
            }
        </div>
    );
};

export default ApplicationPage;